import React, { useEffect, useState, useMemo } from 'react'
import axios from 'axios'
import { useParams, useNavigate, Link } from 'react-router-dom'
import BASE_URL from '../../../config/config'
import { jwtDecode } from "jwt-decode"
import Loader from '../../../components/Loader'

const SingleProyecto = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [proyecto, setProyecto] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const token = localStorage.getItem("AuthToken")
  let tokenInfo = {}
  if (token) {
    try { tokenInfo = jwtDecode(token) } catch { tokenInfo = {} }
  }
  const isAdmin = tokenInfo.role === 'admin'

  useEffect(() => {
    axios.get(`${BASE_URL}/proyecto/${id}`)
      .then(response => {
        setProyecto(response.data.proyecto)
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        setError('Error al cargar el proyecto')
        setLoading(false)
      })
  }, [id])

  // Redirige si no es admin ni usuario autorizado
  useEffect(() => {
    if (!loading && proyecto) {
      if (tokenInfo.role !== "admin" && tokenInfo.usuario !== proyecto.usuario_autorizado) {
        navigate("/dashboard")
      }
    }
  }, [loading, proyecto, tokenInfo, navigate])

  // Guardar cambios (p.ej. facturado)
  const updateCampo = async (field, value) => {
    if (!proyecto) return
    const payload = {
      prototipo: proyecto.prototipo,
      diseno: proyecto.diseno,
      desarrollo: proyecto.desarrollo,
      contenido: proyecto.contenido,
      comprobacion_final: proyecto.comprobacion_final,
      usuario_autorizado: proyecto.usuario_autorizado,
      facturado: typeof proyecto.facturado === 'undefined' ? 0 : proyecto.facturado,
      [field]: value
    }
    try {
      const res = await axios.put(`${BASE_URL}/actualizar-estados/${proyecto.id}`, payload)
      setProyecto(res.data.proyecto)
    } catch (e) {
      console.error('Error al actualizar:', e)
    }
  }

  const toggleFacturado = () => {
    const current = Number(proyecto?.facturado || 0)
    updateCampo('facturado', current === 1 ? 0 : 1)
  }

  const tipo = useMemo(() => (proyecto?.tipo_de_proyecto || '').toLowerCase(), [proyecto])
// debajo de:
const isDiseno = tipo === 'diseno'
const isMarketing = tipo === 'marketing'


// justo debajo de isDiseno / isMarketing
const isFacturado = useMemo(
  () => Number(proyecto?.facturado ?? 0) === 1,
  [proyecto?.facturado]
)

const isListo = useMemo(() => {
  if (isDiseno)   return proyecto?.diseno === 'aceptado'     && isFacturado
  if (isMarketing) return proyecto?.desarrollo === 'aceptado' && isFacturado
  // Vista "desarrollo" u otros: se mantiene la lógica anterior
  return proyecto?.comprobacion_final === 'aceptado'
}, [
  isDiseno,
  isMarketing,
  proyecto?.diseno,
  proyecto?.desarrollo,
  proyecto?.comprobacion_final,
  isFacturado
])


// clase dinámica para el contenedor de la timeline
const timelineClass = useMemo(() => {
  if (isDiseno) return 'timeline-proyecto timeline-proyecto-diseno'
  if (isMarketing) return 'timeline-proyecto timeline-proyecto-marketing'
  return 'timeline-proyecto'
}, [isDiseno, isMarketing])


  if (loading) return <Loader/>
  if (error) return <div>{error}</div>
  if (!proyecto) return <div>No se encontró el proyecto.</div>

  return (
    <main className='site-width'>
      <div className="single-proyecto">
        {isAdmin ? (
          <>
            <h2>{proyecto.nombre}</h2>
            <p className='tipo-proyecto'>
              Tipo de proyecto:
              <b> {
                  proyecto.tipo_de_proyecto === 'diseno' ? 'Diseño gráfico' :
                  proyecto.tipo_de_proyecto === 'marketing' ? 'Marketing/SEO' :
                  proyecto.tipo_de_proyecto === 'desarrollo' ? 'Desarrollo web' :
                  'No especificado'
                  }
              </b>
            </p>
            {
              proyecto.usuario_autorizado ?
              
                <p>Estás visitando el proyecto del cliente <Link to={`/dashboard/users/${proyecto.usuario_autorizado}`}>@{proyecto.usuario_autorizado}</Link>.</p>
              :
                <p>Este proyecto no tiene un usuario asignado.</p>
            }
          </>
        ) : (
          <>
            <h2>¡Bienvenido a tu proyecto {proyecto.nombre}!</h2>
            <p className='tipo-proyecto'>
              Tipo de proyecto:
              <b> {
                  proyecto.tipo_de_proyecto === 'diseno' ? 'Diseño gráfico' :
                  proyecto.tipo_de_proyecto === 'marketing' ? 'Marketing/SEO' :
                  proyecto.tipo_de_proyecto === 'desarrollo' ? 'Desarrollo web' :
                  'No especificado'
                  }
              </b>
            </p>
            <p>Visualiza el estado actual de tu proyecto. Para cualquier consulta, no dudes en abrir un ticket o llamarnos directamente a la extensión correspondiente.</p>
          </>
        )}

        <div className="grid-timeline">
          <div className="texto-pre-timeline">
           {isListo
              ? <p className='fin-proyecto'>¡Tu proyecto finalizó!</p>
              : <p className='warning-proyecto'>{proyecto.prototipo === 'sin_ejecutar' ? 'Este proyecto no se ha empezado.' : 'Tu proyecto no está listo todavía.'}</p>
            }
          </div>

          <div className={timelineClass}>
            {/* -------- VISTA: DESARROLLO (completa) -------- */}
            {!isDiseno && !isMarketing && (
              <>
                {/* Prototipo */}
                <div className={`item-timeline proyecto-${proyecto.prototipo}`}>
                  <div className='icono-timeline'>
                    <img src="/images/cohete.svg" alt="Icono" />
                    {proyecto.prototipo === "aceptado" && (
                      <img src="/images/controlar.svg" className='check-aceptado' alt="OK" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>Fase 1 - Prototipaje</h3>
                    <p>Maquetación del prototipo inicial de la página web.</p>
                    {proyecto.prototipo === "en_proceso" && <p className='texto-timeline-en_proceso'>En proceso…</p>}
                    {proyecto.prototipo === "aceptado" && <p className='texto-timeline-aceptado'>Validado</p>}
                  </div>
                </div>

                {/* Diseño */}
                <div className={`item-timeline proyecto-${proyecto.diseno}`}>
                  <div className='icono-timeline'>
                    <img src="/images/paleta-de-color.svg" alt="Icono" />
                    {proyecto.diseno === "aceptado" && (
                      <img src="/images/controlar.svg" className='check-aceptado' alt="OK" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>Fase 2 - Diseño</h3>
                    <p>Diseño de las páginas necesarias del proyecto basadas en el prototipo.</p>
                    {proyecto.diseno === "en_proceso" && <p className='texto-timeline-en_proceso'>En proceso…</p>}
                    {proyecto.diseno === "aceptado" && <p className='texto-timeline-aceptado'>Validado</p>}
                  </div>
                </div>

                {/* Desarrollo */}
                <div className={`item-timeline proyecto-${proyecto.desarrollo}`}>
                  <div className='icono-timeline'>
                    <img src="/images/codificacion.svg" alt="Icono" />
                    {proyecto.desarrollo === "aceptado" && (
                      <img src="/images/controlar.svg" className='check-aceptado' alt="OK" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>Fase 3 - Desarrollo web</h3>
                    <p>Desarrollo de la página web basado en el diseño y prototipo aceptados.</p>
                    {proyecto.desarrollo === "en_proceso" && <p className='texto-timeline-en_proceso'>En proceso…</p>}
                    {proyecto.desarrollo === "aceptado" && <p className='texto-timeline-aceptado'>Validado</p>}
                  </div>
                </div>

                {/* Contenido */}
                <div className={`item-timeline proyecto-${proyecto.contenido}`}>
                  <div className='icono-timeline'>
                    <img src="/images/lapiz.svg" alt="Icono" />
                    {proyecto.contenido === "aceptado" && (
                      <img src="/images/controlar.svg" className='check-aceptado' alt="OK" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>Fase 4 - Contenido</h3>
                    <p>El sitio web dispone del contenido necesario para funcionar.</p>
                    {proyecto.contenido === "en_proceso" && <p className='texto-timeline-en_proceso'>En proceso…</p>}
                    {proyecto.contenido === "aceptado" && <p className='texto-timeline-aceptado'>Validado</p>}
                  </div>
                </div>

                {/* Validación web */}
                <div className={`item-timeline proyecto-${proyecto.comprobacion_final}`}>
                  <div className='icono-timeline'>
                    <img src="/images/verificar.svg" alt="Icono" />
                    {proyecto.comprobacion_final === "aceptado" && (
                      <img src="/images/controlar.svg" className='check-aceptado' alt="OK" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>Fase 5 - Validación web</h3>
                    <p>Revisión la optimización, indexación básica en motores de búsqueda y políticas legales.</p>
                    {proyecto.comprobacion_final === "en_proceso" && <p className='texto-timeline-en_proceso'>En proceso…</p>}
                    {proyecto.comprobacion_final === "aceptado" && <p className='texto-timeline-aceptado'>Validado</p>}
                  </div>
                </div>
              </>
            )}

            {/* -------- VISTA: DISEÑO -------- */}
            {isDiseno && (
              <>
                {/* Solo Diseño */}
                <div className={`item-timeline proyecto-${proyecto.diseno}`}>
                  <div className='icono-timeline'>
                    <img src="/images/paleta-de-color.svg" alt="Icono" />
                    {proyecto.diseno === "aceptado" && (
                      <img src="/images/controlar.svg" className='check-aceptado' alt="OK" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>Fase - Diseño</h3>
                    <p>Diseño del proyecto.</p>
                    {proyecto.diseno === "en_proceso" && <p className='texto-timeline-en_proceso'>En proceso…</p>}
                    {proyecto.diseno === "aceptado" && <p className='texto-timeline-aceptado'>Validado</p>}
                  </div>
                </div>

                {/* Facturado */}
                <div className={`item-timeline proyecto-facturado`}>
                  <div className='icono-timeline'>
                    <img src="/images/pago.svg" alt="Icono factura" />
                  </div>
                  <div className="texto-timeline">
                    <h3>Facturado</h3>
                    <p>Estado de facturación del proyecto.</p>
                    <p><b>{Number(proyecto.facturado || 0) === 1 ? 'Sí' : 'No'}</b></p>
                  </div>
                </div>
              </>
            )}

            {/* -------- VISTA: MARKETING -------- */}
            {isMarketing && (
              <>
                {/* Solo Desarrollo */}
                <div className={`item-timeline proyecto-${proyecto.desarrollo}`}>
                  <div className='icono-timeline'>
                    <img src="/images/codificacion.svg" alt="Icono" />
                    {proyecto.desarrollo === "aceptado" && (
                      <img src="/images/controlar.svg" className='check-aceptado' alt="OK" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>Fase - Realización</h3>
                    <p>Ejecución técnica de las acciones y entregables.</p>
                    {proyecto.desarrollo === "en_proceso" && <p className='texto-timeline-en_proceso'>En proceso…</p>}
                    {proyecto.desarrollo === "aceptado" && <p className='texto-timeline-aceptado'>Validado</p>}
                  </div>
                </div>

                {/* Facturado */}
                <div className={`item-timeline proyecto-facturado`}>
                  <div className='icono-timeline'>
                    <img src="/images/pago.svg" alt="Icono factura" />
                  </div>
                  <div className="texto-timeline">
                    <h3>Facturado</h3>
                    <p>Estado de facturación del proyecto.</p>
                    <p><b>{Number(proyecto.facturado || 0) === 1 ? 'Sí' : 'No'}</b></p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}

export default SingleProyecto
